import { ChangeEvent, useState } from 'react';

import { DatePicker } from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { EyeIcon, EyeOffIcon } from '../../../../components/Icon';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { custom, required, ssn, ssnConfirmMatch } from '../../utils/fieldValidation';

export interface PersonalDetailsFormData {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  otherNames: string;
  dateOfBirth: string;
  ssn: string;
  ssnConfirm: string;
  hasMiddleName: boolean;
}

const suffixOptions = [
  { label: 'Select an option', value: '' },
  { label: 'Jr.', value: 'Jr' },
  { label: 'Sr.', value: 'Sr' },
  { label: 'II', value: 'II' },
  { label: 'III', value: 'III' },
  { label: 'IV', value: 'IV' },
  { label: 'V', value: 'V' },
  { label: 'VI', value: 'VI' },
  { label: 'VII', value: 'VII' },
  { label: 'VIII', value: 'VIII' },
  { label: 'IX', value: 'IX' },
  { label: 'X', value: 'X' },
  { label: 'XI', value: 'XI' },
];

const hasMiddleNameOptions = [
  { label: 'Select an option', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const MINIMUM_AGE = 18;
const MAXIMUM_AGE = 100;

function MyPersonalDetailsPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<PersonalDetailsFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const [isSSNVisible, setIsSSNVisible] = useState(false);
  const [isSSNConfirmVisible, setIsSSNConfirmVisible] = useState(false);
  const baseTestId = 'my-personal-details-page';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'firstName', label: 'Legal First Name', validationRules: [required] },
      { field: 'lastName', label: 'Legal Last Name', validationRules: [required] },
      { field: 'dateOfBirth', label: 'Date of Birth', validationRules: [required] },
      { field: 'ssn', label: 'Social Security Number', validationRules: [required, ssn] },
      {
        field: 'ssnConfirm',
        label: 'Re-enter your Social Security Number',
        validationRules: [required, ssn, ssnConfirmMatch],
      },
      {
        field: 'middleName',
        label: 'Legal Middle Name',
        validationRules: [
          custom((value: unknown, allData?: Record<string, unknown>) => {
            if (allData?.hasMiddleName) {
              if (typeof value === 'string') {
                return value.trim().length > 0;
              }

              return false;
            }

            return true;
          }, 'Legal Middle Name is required'),
        ],
      },
    ],
  });

  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - MAXIMUM_AGE);
  const maxDate = new Date();
  maxDate.setFullYear(today.getFullYear() - MINIMUM_AGE);

  const handleChange = (field: keyof PersonalDetailsFormData, value: string) => {
    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  const handleSSNKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const value = input.value;
    const currentSSN = data.ssn || '';

    if (e.key === 'Backspace' && cursorPos > 0) {
      // If the character before cursor is a dash, skip over it and delete the digit before
      if (value[cursorPos - 1] === '-') {
        e.preventDefault();
        if (currentSSN.length > 0) {
          updateData({
            ...data,
            ssn: currentSSN.slice(0, -1),
          });
        }
      }
    }
  };

  const handleSSNChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const currentSSN = data.ssn || '';
    const inputWithoutDashes = input.replace(/-/g, '');
    const inputLength = inputWithoutDashes.length;
    const storedLength = currentSSN.length;

    if (inputLength > storedLength) {
      const newDigit = inputWithoutDashes.replace(/\*/g, '').slice(-1);

      if (/^\d$/.test(newDigit) && currentSSN.length < 9) {
        const newSSN = currentSSN + newDigit;
        updateData({
          ...data,
          ssn: newSSN,
        });
        handleValidationChange('ssn', newSSN);
      }
    } else if (inputLength < storedLength) {
      const newSSN = currentSSN.slice(0, inputLength);
      updateData({
        ...data,
        ssn: newSSN,
      });
      handleValidationChange('ssn', newSSN);
    }
  };

  const getFormattedSSN = (): string => {
    const value = data.ssn || '';
    if (!value) return '';

    // If SSN is visible, show all digits; otherwise mask all but the last one
    const displayDigits = isSSNVisible
      ? value.split('')
      : value.split('').map((digit, index) => {
          // Show the last digit, mask all others with asterisks
          return index === value.length - 1 ? digit : '*';
        });

    // Format with dashes: XXX-XX-XXXX
    if (value.length <= 2) {
      return displayDigits.join('');
    } else if (value.length === 3) {
      return `${displayDigits.slice(0, 3).join('')}-`;
    } else if (value.length === 4) {
      return `${displayDigits.slice(0, 3).join('')}-${displayDigits.slice(3).join('')}`;
    } else if (value.length === 5) {
      return `${displayDigits.slice(0, 3).join('')}-${displayDigits.slice(3, 5).join('')}-`;
    } else {
      return `${displayDigits.slice(0, 3).join('')}-${displayDigits.slice(3, 5).join('')}-${displayDigits
        .slice(5)
        .join('')}`;
    }
  };

  const toggleSSNVisibility = () => {
    setIsSSNVisible((prev) => !prev);
  };

  // Confirm SSN handlers
  const handleSSNConfirmKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const value = input.value;
    const currentSSNConfirm = data.ssnConfirm || '';

    if (e.key === 'Backspace' && cursorPos > 0) {
      if (value[cursorPos - 1] === '-') {
        e.preventDefault();
        if (currentSSNConfirm.length > 0) {
          updateData({
            ...data,
            ssnConfirm: currentSSNConfirm.slice(0, -1),
          });
        }
      }
    }
  };

  const handleSSNConfirmChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const currentSSNConfirm = data.ssnConfirm || '';
    const inputWithoutDashes = input.replace(/-/g, '');
    const inputLength = inputWithoutDashes.length;
    const storedLength = currentSSNConfirm.length;

    if (inputLength > storedLength) {
      const newDigit = inputWithoutDashes.replace(/\*/g, '').slice(-1);

      if (/^\d$/.test(newDigit) && currentSSNConfirm.length < 9) {
        const newSSNConfirm = currentSSNConfirm + newDigit;
        updateData({
          ...data,
          ssnConfirm: newSSNConfirm,
        });
        handleValidationChange('ssnConfirm', newSSNConfirm);
      }
    } else if (inputLength < storedLength) {
      const newSSNConfirm = currentSSNConfirm.slice(0, inputLength);
      updateData({
        ...data,
        ssnConfirm: newSSNConfirm,
      });
      handleValidationChange('ssnConfirm', newSSNConfirm);
    }
  };

  const getFormattedSSNConfirm = (): string => {
    const value = data.ssnConfirm || '';
    if (!value) return '';

    const displayDigits = isSSNConfirmVisible
      ? value.split('')
      : value.split('').map((digit, index) => {
          return index === value.length - 1 ? digit : '*';
        });

    if (value.length <= 2) {
      return displayDigits.join('');
    } else if (value.length === 3) {
      return `${displayDigits.slice(0, 3).join('')}-`;
    } else if (value.length === 4) {
      return `${displayDigits.slice(0, 3).join('')}-${displayDigits.slice(3).join('')}`;
    } else if (value.length === 5) {
      return `${displayDigits.slice(0, 3).join('')}-${displayDigits.slice(3, 5).join('')}-`;
    } else {
      return `${displayDigits.slice(0, 3).join('')}-${displayDigits.slice(3, 5).join('')}-${displayDigits
        .slice(5)
        .join('')}`;
    }
  };

  const toggleSSNConfirmVisibility = () => {
    setIsSSNConfirmVisible((prev) => !prev);
  };

  const handleHasMiddleNameChange = (value: boolean | null) => {
    updateData({
      ...data,
      hasMiddleName: value === true,
      middleName: value === true ? data.middleName : '',
    });
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-first-name`}>
        <TextField
          required
          data-testid={`${baseTestId}-first-name-field`}
          errorMessage={getFieldError('firstName')}
          label='Legal First Name'
          maxLength={100}
          name='firstName'
          value={data.firstName || ''}
          onBlur={() => handleBlur('firstName')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('firstName', e.target.value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-middle-name`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-middle-name-dropdown`}
          errorMessage={fieldErrors?.hasMiddleName}
          label='Do you have a Legal Middle Name?'
          name='hasMiddleName'
          options={hasMiddleNameOptions}
          value={data.hasMiddleName === true ? true : data.hasMiddleName === false ? false : null}
          onChange={handleHasMiddleNameChange}
        />
        {data.hasMiddleName === true && (
          <div
            className='ds-u-margin-top--3'
            data-testid={`${baseTestId}-middle-name-field-wrapper`}>
            <TextField
              required
              data-testid={`${baseTestId}-middle-name-field`}
              errorMessage={getFieldError('middleName')}
              label='Legal Middle Name'
              maxLength={100}
              name='middleName'
              tooltipText='Enter your legal middle name as it appears on your official identification documents.'
              value={data.middleName || ''}
              onBlur={() => handleBlur('middleName')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('middleName', e.target.value)}
            />
          </div>
        )}
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-last-name`}>
        <TextField
          required
          data-testid={`${baseTestId}-last-name-field`}
          errorMessage={getFieldError('lastName')}
          label='Legal Last Name'
          maxLength={100}
          name='lastName'
          value={data.lastName || ''}
          onBlur={() => handleBlur('lastName')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('lastName', e.target.value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-suffix`}>
        <Dropdown
          data-testid={`${baseTestId}-suffix-dropdown`}
          label='Suffix'
          name='suffix'
          options={suffixOptions}
          value={data.suffix || ''}
          onChange={(value: string) => handleChange('suffix', value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-other-names`}>
        <TextField
          autoComplete='off'
          data-testid={`${baseTestId}-other-names-field`}
          hint='List any other names you have used in the past'
          label='Other Names Used'
          name='otherNames'
          tooltipText='Include any previous names, maiden names, or aliases you have used in the past.'
          value={data.otherNames || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('otherNames', e.target.value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-dob`}>
        <DatePicker
          required
          data-testid={`${baseTestId}-dob-picker`}
          errorMessage={getFieldError('dateOfBirth')}
          fromDate={minDate}
          id='dateOfBirth'
          label='Date of Birth'
          name='dateOfBirth'
          toDate={maxDate}
          tooltipText='Enter your date of birth in MM/DD/YYYY format. You must be at least 18 years old.'
          value={data.dateOfBirth || ''}
          onBlur={() => handleBlur('dateOfBirth')}
          onDateChange={(date, formattedDate) => handleChange('dateOfBirth', formattedDate || '')}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-ssn`}>
        <TextField
          required
          autoComplete='off'
          data-testid={`${baseTestId}-ssn-field`}
          errorMessage={getFieldError('ssn')}
          hint='XXX-XX-XXXX'
          label='Social Security Number'
          name='ssn'
          rightIcon={isSSNVisible ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
          rightIconAriaLabel={isSSNVisible ? 'Hide Social Security Number' : 'Show Social Security Number'}
          tooltipText='Your Social Security Number is required for identity verification and background checks. This information is kept secure and confidential.'
          type='text'
          value={getFormattedSSN()}
          onBlur={() => handleBlur('ssn')}
          onChange={handleSSNChange}
          onKeyDown={handleSSNKeyDown}
          onRightIconClick={toggleSSNVisibility}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-ssn-confirm`}>
        <TextField
          required
          autoComplete='off'
          data-testid={`${baseTestId}-ssn-confirm-field`}
          errorMessage={getFieldError('ssnConfirm')}
          hint='XXX-XX-XXXX'
          label='Re-enter your Social Security Number'
          name='ssnConfirm'
          rightIcon={isSSNConfirmVisible ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
          rightIconAriaLabel={
            isSSNConfirmVisible ? 'Hide re-entered Social Security Number' : 'Show re-entered Social Security Number'
          }
          tooltipText='You must re-enter your Social Security Number into the field.'
          type='text'
          value={getFormattedSSNConfirm()}
          onBlur={() => handleBlur('ssnConfirm')}
          onChange={handleSSNConfirmChange}
          onKeyDown={handleSSNConfirmKeyDown}
          onRightIconClick={toggleSSNConfirmVisibility}
        />
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: My Personal Details'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Personal Details</h3>
          <p>Please enter your full legal name as it appears on your official documents. These may include:</p>
          <ul>
            <li>Driver&apos;s license</li>
            <li>Passport</li>
            <li>Birth certificate</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Legal First Name</h4>
          <p>Enter your first name as it appears on your official ID documents.</p>

          <h4 className='ds-u-margin-top--3'>Legal Middle Name</h4>
          <p>
            Choose an option from the dropdown menu. If you do not have a Legal Middle Name, select "No" and move to the
            next question. If you do have a Legal Middle Name, select "Yes" or "My Legal Middle Name is an initial,"
            then enter your legal middle name as it is shown on official documents in the textbox that appears. You must
            enter your full legal middle name as it appears on your official ID documents.
          </p>

          <h4 className='ds-u-margin-top--3'>Legal Last Name</h4>
          <p>Enter your last name (surname/family name) as it appears on your official ID documents.</p>

          <h4 className='ds-u-margin-top--3'>Suffix</h4>
          <p>If you have one, choose your name suffix (Jr., Sr., II, III, IV) from the dropdown.</p>

          <h4 className='ds-u-margin-top--3'>Other Names Used</h4>
          <p>
            Enter a name other than your current legal name that you have been known by. Use a comma (,) between each
            name entered. This will be used to verify your identity and employment history. These may include:
          </p>
          <ul>
            <li>Maiden names</li>
            <li>Aliases</li>
            <li>Professional names</li>
            <li>Religious names</li>
            <li>Nicknames used on official documents</li>
            <li>Names from prior marriages</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Date of Birth</h4>
          <p>Enter your date of birth in MM/DD/YYYY format. You must be at least 18 years old.</p>

          <h4 className='ds-u-margin-top--3'>Social Security Number</h4>
          <p>
            Enter your Social Security Number (with or without dashes). This will be used to verify your identity and
            run background checks. Your data will remain protected and private.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default MyPersonalDetailsPage;
